<template>
  <div v-if="step" class="step-datetime-input">
    <Datepicker v-model="date" :placeholder="step.title" />
    <TimeInput v-model="time" />
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { ref, watch } from "vue";
import { currentTenantTimezone } from "@/lib/helpers";

import Datepicker from "@/components/Seranova/Datepicker/Datepicker.vue";
import TimeInput from "@/components/Seranova/TimeInput/TimeInput.vue";
import { TimelineStep } from "@/typings/scheduler";

const emit = defineEmits(["update:modelValue"]);
const props = defineProps<{ step: TimelineStep; defaultDate?: string }>();
const date = ref(props.step?.date || props.defaultDate || "");
const time = ref(props.step?.time || "");

// Compute the ISO string the step was initialized from so we can detect
// whether a change is a real edit or just a mount-time reformat by
// Datepicker/TimeInput normalizing their display values.
const initialIso =
  props.step?.date && props.step?.time
    ? moment
        .tz(
          props.step.date + " " + props.step.time,
          "MM/DD/YY h:mmA",
          currentTenantTimezone
        )
        .toISOString()
    : null;

watch(date, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    emitInput();
  }
});
watch(time, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    emitInput();
  }
});
const emitInput = () => {
  const fieldName = props.step?.editableFieldName;
  if (!date.value || !time.value) {
    if (initialIso !== null) {
      emit("update:modelValue", { [fieldName]: null });
    }
    return;
  }
  const timeStr = moment
    .tz(date.value + " " + time.value, "MM/DD h:mm A", currentTenantTimezone)
    .toISOString();
  if (timeStr === initialIso) return;
  emit("update:modelValue", { [fieldName]: timeStr });
};
</script>

<style scoped>
.step-datetime-input {
  display: contents;
}
</style>
